import { useState, useEffect } from 'react';
import { Package } from 'lucide-react';
import { getLatestDependencyScan } from '../../../services/firestore/opsService';
import type { DependencyScan, DependencyEntry } from '../../../types/ops';

const C = {
  text: '#18181B', textSec: '#52525B', textMuted: '#A1A1AA',
  rose: '#E11D48', pass: '#059669', warn: '#B45309',
  bgCard: '#FFFFFF', border: '#E4E4E7',
};

const STATUS_COLORS: Record<string, { color: string; bg: string; label: string }> = {
  current: { color: C.pass, bg: '#D1FAE5', label: 'CURRENT' },
  minor_behind: { color: C.warn, bg: '#FEF3C7', label: 'MINOR' },
  major_behind: { color: C.rose, bg: '#FFF1F2', label: 'MAJOR' },
  vulnerable: { color: '#DC2626', bg: '#FEF2F2', label: 'VULN' },
  deprecated: { color: '#71717A', bg: '#F4F4F5', label: 'DEPRECATED' },
};

export function DependenciesTab() {
  const [scan, setScan] = useState<DependencyScan | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setScan(await getLatestDependencyScan());
      } catch (err) {
        console.error('Failed to load dependency scan:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div style={{ textAlign: 'center', padding: 60, color: C.textMuted }}>Loading dependencies...</div>;

  if (!scan) {
    return (
      <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 40, textAlign: 'center' }}>
        <Package size={32} style={{ color: C.textMuted, marginBottom: 12 }} />
        <p style={{ fontSize: 16, fontWeight: 600, color: C.text, marginBottom: 8 }}>No dependency scan yet</p>
        <p style={{ fontSize: 14, color: C.textSec }}>
          The weekly scan will check all dependencies against the npm registry. First scan runs Monday 3 AM UTC.
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Summary */}
      <div style={{ display: 'flex', gap: 16, marginBottom: 16 }}>
        <StatCard label="Total Dependencies" value={scan.clientDeps.length + scan.functionsDeps.length} />
        <StatCard label="Outdated" value={scan.totalOutdated} color={scan.totalOutdated > 0 ? C.warn : C.pass} />
        <StatCard label="Vulnerable" value={scan.totalVulnerable} color={scan.totalVulnerable > 0 ? C.rose : C.pass} />
        <StatCard label="Last Scan" value={new Date(scan.scannedAt).toLocaleDateString()} />
      </div>

      {/* Client deps */}
      <DepTable title="Client Dependencies" deps={scan.clientDeps} />

      {/* Functions deps */}
      <DepTable title="Cloud Functions Dependencies" deps={scan.functionsDeps} />
    </div>
  );
}

function DepTable({ title, deps }: { title: string; deps: DependencyEntry[] }) {
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 20, marginBottom: 16 }}>
      <p style={{ fontSize: 13, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 1, color: C.textMuted, marginBottom: 12 }}>{title}</p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Th>Package</Th>
            <Th>Current</Th>
            <Th>Latest</Th>
            <Th>Status</Th>
            <Th>License</Th>
          </tr>
        </thead>
        <tbody>
          {deps.map((dep) => {
            const status = STATUS_COLORS[dep.status] || STATUS_COLORS.current;
            return (
              <tr key={dep.name} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: '10px 12px', fontSize: 14, fontWeight: 500, color: C.text }}>{dep.name}</td>
                <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textSec }}>{dep.currentVersion}</td>
                <td style={{ padding: '10px 12px', fontSize: 13, fontFamily: "'JetBrains Mono', monospace", color: C.textSec }}>{dep.latestVersion}</td>
                <td style={{ padding: '10px 12px' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 600, color: status.color, background: status.bg,
                    padding: '2px 8px', borderRadius: 4,
                  }}>
                    {status.label}
                  </span>
                </td>
                <td style={{ padding: '10px 12px', fontSize: 13, color: C.textMuted }}>{dep.license}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div style={{ background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4, padding: 16, flex: 1, textAlign: 'center' }}>
      <p style={{ fontSize: 22, fontWeight: 700, color: color || C.text }}>{value}</p>
      <p style={{ fontSize: 12, color: C.textMuted }}>{label}</p>
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th style={{
      textAlign: 'left', fontSize: 12, fontWeight: 600, textTransform: 'uppercase',
      letterSpacing: 0.5, color: '#A1A1AA', padding: '8px 12px', borderBottom: '1px solid #E4E4E7',
    }}>
      {children}
    </th>
  );
}
